
import React, { useState } from 'react';
import { SimulationConfig, MelodyData } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { SmartInput } from './SmartInput';
import { AUDIO } from './AudioService';

interface RhythmicModeSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const RhythmicModeSection: React.FC<RhythmicModeSectionProps> = ({ config, setConfig }) => {
  const [jsonInput, setJsonInput] = useState<string>('');

  const loadData = () => {
    try {
        // Try parsing as JSON first
        const parsed = JSON.parse(jsonInput);
        if (Array.isArray(parsed)) {
            setConfig(prev => ({ 
                ...prev, 
                customFrequencies: parsed, 
                selectedMelody: 'Custom (Uploaded)' 
            }));
            alert("Rhythmic Data Loaded Successfully!");
            AUDIO.resetIndex();
        } else {
            alert("Invalid Format: Must be an array []");
        }
    } catch (e) {
        alert("JSON Parse Error. Please check the format.");
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-pink-600/10 border-pink-500/30 mb-4 shadow-xl">
      <SectionLabel icon="fa-music" darkMode={config.darkMode}>Rhythmic Visualizer Mode</SectionLabel>
      
      <div className="bg-black/20 p-3 rounded-xl border border-white/10 mb-2">
        <p className="text-[9px] font-mono opacity-70 mb-2">
            Paste JSON Data here. Format:<br/>
            <code>[{`{"notes":[60], "duration":500}`}, ...]</code>
        </p>
        <textarea 
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            className={`w-full h-32 p-2 text-[10px] font-mono rounded-lg border outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-700 text-green-400' : 'bg-white border-neutral-300 text-black'}`}
            placeholder='[{"notes": [60], "duration": 500}, ...]'
        />
        <button onClick={loadData} className="w-full mt-2 py-2 bg-pink-600 hover:bg-pink-500 text-white rounded-lg text-[10px] font-black uppercase transition-all">
            Load Rhythm Data
        </button>
      </div>

      <div className="flex flex-col gap-3">
          <SectionLabel icon="fa-ruler-combined" darkMode={config.darkMode}>Sync Controls</SectionLabel>
          
          <SmartInput 
            label="Base Circle Size" 
            min={50} max={300} step={10}
            value={config.containerRadius} 
            onChange={v => setConfig(p => ({...p, containerRadius: v}))} 
            darkMode={config.darkMode} 
          />
          <p className="text-[8px] opacity-50 text-right -mt-2">Sets the visual scale. Speed adapts automatically.</p>

          <SmartInput 
            label="Playback Speed" 
            min={0.1} max={2.0} step={0.1}
            value={config.rhythmicTimeScale || 1.0} 
            onChange={v => setConfig(p => ({...p, rhythmicTimeScale: v}))} 
            darkMode={config.darkMode} 
          />
          <p className="text-[8px] opacity-50 text-right -mt-2">1.0 = Realtime. 0.5 = Half Speed.</p>
      </div>
    </div>
  );
};
